import React from 'react';
import { Phone } from 'lucide-react';

const SerengentiSafari = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">9 DAYS SERENGETI WILDBEEST MIGRATION SAFARI</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Main Title */}
        <h3 className="text-3xl font-bold text-[#2f4f2f] mb-6">
                 9 Days Serengeti Wildebeest Migration Safari – Mara River Crossing Tour
        </h3>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Main Content - Left Side */}
          <div className="flex-1">
            {/* Description */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Embark on a captivating 9-day adventure through Tanzania's Serengeti National Park, designed to offer you an extraordinary opportunity to 
                track the awe-inspiring <strong>wildebeest migration</strong>. This adventure places us at the epicenter of this breathtaking natural phenomenon, 
                allowing us to marvel at the expansive Serengeti landscape. Experience expert guides who understand the northern Serengeti, where the 
                great migration unfolds in all its glory.
              </p>
            </div>

            {/* Placeholder for Wildebeest Image */}
            <div className="mb-8">
              <div className="bg-[#f5f5f5] h-64 rounded-lg flex items-center justify-center text-gray-500 text-sm">
               <img  src="/migration2.JPG" alt="9 Days "  className="w-full h-48 object-cover"/>
              </div>
              <p className="text-xs text-gray-500 mt-1">Wildebeest migration river crossing</p>
            </div>

            {/* Itinerary Title */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 9 Days Serengeti Wildebeest Migration Safari
            </h3>

            {/* Day 1 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrival in Arusha</h4>
              <p className="text-gray-700 leading-relaxed">
                Our driver guide will welcome and greet you at the airport and later drive you to your lodging in Arusha. Here, you can unwind from 
                your journey, soak in the local atmosphere and get ready for the <span className="text-[#9acd32]">thrilling wildlife safari</span> that awaits. This evening offers a perfect 
                opportunity to relax and acclimate before we embark on our <span className="text-[#9acd32]">Serengeti adventure</span>.
              </p>
            </div>

            {/* Day 2 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Arusha to Tarangire National Park</h4>
              <p className="text-gray-700 leading-relaxed">
                Start your day in Arusha as your personal driver guide picks you up and set off towards <span className="text-[#9acd32]">Tarangire National Park</span>. The journey takes 
                about two and a half hours of scenic landscapes towards Tarangire with an encounter of various endemic bird species and tree climbing 
                lions among the acacia trees. Sing hill city you will encounter a rich diversity of wildlife.<br/><br/>
                Upon reaching <span className="text-[#9acd32]">Tarangire National Park</span>, famous for its vast elephant populations, you will encounter a rich diversity of wildlife. From 
                massive baobab trees to various endemic bird species and seasonal wetlands, migrating seasons, and the elusive 
                <span className="text-[#9acd32]">Tarangire River</span>, keep your eyes peeled for a variety of animals including zebras, wildebeests, buffaloes, elephants, and the not 
                major cats can be heard or leopards lounging at the trees.<br/><br/>
                After a fulfilling day of <span className="text-[#9acd32]">wildlife viewing</span>, you will head to your lodge for dinner and a restful overnight stay, ready for more 
                adventures tomorrow.
              </p>
            </div>

            {/* Day 3 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Transfer to Serengeti National Park</h4>
              <p className="text-gray-700 leading-relaxed">
                After breakfast, you will journey <span className="text-[#9acd32]">into the lush highlands</span> where the Iraqw people cultivate wheat and corn. En route, we will 
                experience the misty conditions of the <span className="text-[#9acd32]">Ngorongoro Conservation Area</span>, keep an eye out for wildlife hidden in the dense foliage, you 
                may catch glimpses of leopards and elephants grazing in the forests at the rim of the crater.<br/><br/>
                As we pass through the conservation area, you will be treated to a <span className="text-[#9acd32]">stunning view of the Ngorongoro Crater</span>, a geological wonder 
                that offers breathtaking vistas from the rim. At the Serengeti Visitor Center, we will be able to appreciate the fact that the 
                <span className="text-[#9acd32]">park is Tanzania's largest</span>, boasts a tapestry of landscapes from sweeping grasslands, and wetlands to lakes, savannas, and rugged 
                kopjes.<br/><br/>
                Spend the afternoon exploring the diverse habitats of the Serengeti on an <span className="text-[#9acd32]">extensive game drive</span>. As the sun sets, we will head to 
                your lodge where a hearty dinner awaits and a peaceful night's rest awaits.
              </p>
            </div>

            {/* Day 4 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 4: Game Drives in the Northern Serengeti</h4>
              <p className="text-gray-700 leading-relaxed">
                In the morning, we begin our journey northward through the Serengeti, <span className="text-[#9acd32]">embarking on an exciting game drive</span>. The region, extending 
                along the Mara River, boasts a stunning array of wildlife species year-round, surrounded by granite outcroppings known 
                as kopjes. Home to a rich and varied wildlife population, <span className="text-[#9acd32]">the Northern Serengeti</span> offers a tapestry of natural beauty, including the 
                famous wildebeest crossing.<br/><br/>
                Unlike the more frequented southern areas, the northern regions provide a tranquil and less crowded <span className="text-[#9acd32]">safari experience</span>. In 
                August, the annual migration brings a surge of wildlife to the river, with hundreds of thousands crossing the Mara River navigating 
                powerful currents and evading crocodiles.<br/><br/>
                At the conclusion of our exhilarating day of exploration and wildlife encounters, we will make our way to our lodging. Henrietta awaits a 
                delicious dinner and rest under the awe-inspiring African night sky, nestled in the heart of the <span className="text-[#9acd32]">Serengeti's Endless Plain</span>.
              </p>
            </div>

            {/* Day 5 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 5: Witness Serengeti Mara River Crossing</h4>
              <p className="text-gray-700 leading-relaxed">
                Starting the day with an early breakfast, you will set out for a thrilling morning in the wild. If luck is on your side, you might <span className="text-[#9acd32]">witness 
                one of nature's most dramatic events: the Mara River crossing</span>. As hundreds of thousands of wildebeest stream into the river, 
                they dance dangerously at the crocodile grounds at the opposite side, yet hesitate to cross the treacherous, chocolate-infested waters.<br/><br/>
                In addition to out of bounds, one wildebeest might leap into the current, prompting a tidal wave of followers to plunge in behind. 
                Those that survive the perilous journey across the river will find themselves grazing on the lush, green pastures of the Masai Mara, 
                as well-roasted from the Grumeti River.
              </p>
            </div>

            {/* Day 6 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 6: North to Central Serengeti</h4>
              <p className="text-gray-700 leading-relaxed">
                After breakfast, we will journey back to Seronera, allowing more time to <span className="text-[#9acd32]">marvel at the Serengeti's extraordinary wildlife and 
                breathtaking scenery</span>. Beyond the migration herds, this region offers a diverse landscape teeming with resident wildlife: lions, 
                leopards, cheetahs, and herds engaged in their daily lives, embodying the dynamic cycle of nature.<br/><br/>
                After an immersive game drive, we will proceed to our lodge, where a satisfying dinner and a cozy evening by the fire awaits. As we 
                settle in for the night, you can watch the mesmerizing glow over the endless plains.
              </p>
            </div>

            {/* Day 7 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 7: Central Serengeti to Ngorongoro Conservation Area</h4>
              <p className="text-gray-700 leading-relaxed">
                After breakfast, your adventure continues with another spectacular game drive through the Serengeti, offering more opportunities 
                to witness the park's incredible wildlife. You will have the chance to spot the famous Big Five: lions, leopards, elephants, buffaloes, and rhinos. 
                <span className="text-[#9acd32]">Crater</span>, a UNESCO World Heritage site renowned for its unique ecosystem and stunning landscapes.<br/><br/>
                By afternoon, you will arrive at the crater's edge, where a breathtaking panorama of lush grasslands, shimmering streams, and a 
                wealth of wildlife spreads below, giving you a preview of the wonders to come the next day. Tonight, you will stay at a lodge 
                perched on the crater's rim where you can marvel and soak in the views, anticipating tomorrow's exploration.
              </p>
            </div>

            {/* Day 8 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 8: Ngorongoro Crater</h4>
              <p className="text-gray-700 leading-relaxed">
                At the break of dawn, following an early breakfast, we will descend into <span className="text-[#9acd32]">the depths of the Ngorongoro Crater</span> for an unforgettable 
                day of wildlife exploration. This ancient caldera, formed millions of years ago by a volcanic eruption, serves as a natural sanctuary for 
                Kilimanjaro, plunges 610 meters deep and spans approximately 260 square kilometers. Its unique geography creates a self-
                contained ecosystem where a remarkable array of animals and birds thrive within its boundaries, offering unparalleled 
                opportunities for unforgettable wildlife viewing.<br/><br/>
                We spend the morning to early afternoon, we will make our way toward the Maasai community along through the crater's vast expanse. 
                Renowned as one of Africa's prime destinations to spot <span className="text-[#9acd32]">the iconic Big Five</span> – lions, elephants, buffaloes, leopards, and rhinoceros. 
                The crater also serves and supports thousands and diverse wildlife species in spectacular beauty.<br/><br/>
                After a delightful picnic lunch by the hippo-filled lake, we will spend the afternoon ascending the steep crater walls, marveling at 
                the panoramic vistas before evening to Karatu for an overnight stay.
              </p>
            </div>

            {/* Day 9 */}
            <div className="mb-6 p-4 border border-[#9acd32]/20 rounded-lg">
              <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 9: Lake Manyara National Park to Arusha</h4>
              <p className="text-gray-700 leading-relaxed">
                After a satisfying breakfast, we will embark on the final leg of our extraordinary <span className="text-[#9acd32]">safari adventure to Lake Manyara National Park</span>. 
                Located 120km west of Arusha, <span className="text-[#9acd32]">this compact yet diverse park</span> is renowned for <span className="text-[#9acd32]">its namesake alkaline salt lake</span>, which fluctuates 
                in size each year. The shoreline, lined with pink flamingos, forms the stunning backdrop of Tarangire and an impressive array of 
                diverse landscapes.<br/><br/>
                As we explore the park, expect to encounter a variety of wildlife including monkeys, giraffes, zebras, wildebeests, buffaloes, 
                hippos, and if we are fortunate, lions lazing in the trees. <span className="text-[#9acd32]">Lake Manyara National Park</span> is celebrated for its famous bird watching 
                opportunities open among plants, wetlands, shining with primates, and dramatic cliffs adorned with ancient baobab trees.<br/><br/>
                Following a delightful lunch, we will journey back to Arusha, arriving in the late afternoon, <span className="text-[#9acd32]">concluding our safari</span> with memories of 
                a lifetime adventure and optimal.
              </p>
            </div>

            {/* Inclusions Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-[#2f4f2f] mb-4">Inclusions</h3>
              <ul className="text-gray-700 space-y-1 text-sm">
                <li>• Transportation on a 4 x 4 safari vehicle with a pop up roof</li>
                <li>• Accommodations and meals as stated</li>
                <li>• Entrance fees to all National Parks</li>
                <li>• Professional driver guide</li>
                <li>• Bottled water</li>
                <li>• Professional English speaking driver guide</li>
                <li>• English Driver guide</li>
              </ul>
            </div>

            {/* Exclusions Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-[#2f4f2f] mb-4">Exclusions</h3>
              <ul className="text-gray-700 space-y-1 text-sm">
                <li>• Visa Fees & flights</li>
                <li>• Optional activities</li>
                <li>• Tips for guides</li>
                <li>• Laundry</li>
                <li>• Alcoholic drinks</li>
                <li>• International vaccinations & meds</li>
                <li>• Items of personal nature</li>
                <li>• Personal travel insurance</li>
              </ul>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-full md:w-80 p-4">
            {/* Trip Info Box */}
            <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4">
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-[#2f4f2f]">9</div>
                <div className="text-sm text-gray-600">Days</div>
                <div className="text-lg font-semibold text-[#2f4f2f] mt-2">Serengeti<br/>Wildebeest Migration Safari</div>
                <div className="text-sm text-gray-600 mt-2">Duration<br/>9 Days & 8 Nights safari</div>
                <div className="text-sm text-gray-600 mt-2">Departure / Return</div>
                <div className="text-sm text-gray-700">Hotel pick-up / Your side departure to your hotel</div>
              </div>

              <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-bold py-3 px-4 rounded transition-colors">
                Plan Your Trip Now!
              </button>

              <div className="text-center mt-4">
                <div className="text-sm text-gray-600 mb-1">Call/WhatsApp for Free Safari Quote</div>
                <div className="text-lg font-bold text-[#2f4f2f]">
                  <Phone className="inline mr-2" size={20} />
                  <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SerengentiSafari;